import { z } from 'zod';
export { z } from 'zod';
import { MAX_PAGE_SIZE, DEFAULT_PAGE_SIZE, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, EducationLevel, TutorStudentStatus, LessonStatus, HomeworkStatus, AssignmentStatus, FilePurpose, MAX_UPLOAD_BYTES, ALLOWED_UPLOAD_MIME_TYPES, WhiteboardToolType } from '@tutor/shared';

// src/common.ts
var uuidSchema = z.string().uuid("Must be a valid UUID");
var isoDateSchema = z.string().datetime({ offset: true }).or(z.string().datetime()).describe("ISO-8601 timestamp");
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE)
});
var idParamSchema = z.object({ id: uuidSchema });
var lessonIdParamSchema = z.object({ lessonId: uuidSchema });
var studentIdParamSchema = z.object({ studentId: uuidSchema });
var homeworkIdParamSchema = z.object({ homeworkId: uuidSchema });
var shortText = (max = 255) => z.string().trim().min(1).max(max);
var optionalShortText = (max = 255) => z.string().trim().max(max).optional().transform((v) => v === "" ? void 0 : v);
var longText = (max = 1e4) => z.string().trim().max(max);
var emailSchema = z.string().trim().toLowerCase().min(1, "Email address is required").max(254).email("Enter a valid email address");
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required").max(PASSWORD_MAX_LENGTH)
});
var strongPasswordSchema = z.string().min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(PASSWORD_MAX_LENGTH).regex(/[a-z]/, "Password must contain a lowercase letter").regex(/[A-Z]/, "Password must contain an uppercase letter").regex(/[0-9]/, "Password must contain a number").regex(/[^A-Za-z0-9]/, "Password must contain a symbol");
var registerSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(80),
  lastName: z.string().trim().min(1, "Last name is required").max(80),
  email: emailSchema,
  password: strongPasswordSchema,
  confirmPassword: z.string().min(1, "Please confirm your password"),
  role: z.enum(["TUTOR", "STUDENT"], {
    errorMap: () => ({ message: "Choose whether you are a tutor or a student" })
  }),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: "Please accept the terms to continue" })
  }),
  timezone: z.string().trim().max(64).optional(),
  // Optional role-specific extras, applied to the matching profile on creation.
  headline: z.string().trim().max(160).optional(),
  educationLevel: z.enum(["KS2", "KS3", "GCSE", "A_LEVEL", "IB", "UNIVERSITY", "OTHER"]).optional(),
  yearGroup: z.string().trim().max(32).optional(),
  school: z.string().trim().max(160).optional()
}).refine((v) => v.password === v.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var forgotPasswordSchema = z.object({
  email: emailSchema
});
var resetPasswordSchema = z.object({
  token: z.string().min(16, "Reset link is invalid or incomplete"),
  password: strongPasswordSchema,
  confirmPassword: z.string()
}).refine((v) => v.password === v.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  password: strongPasswordSchema,
  confirmPassword: z.string()
}).refine((v) => v.password === v.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var updateProfileSchema = z.object({
  firstName: shortText(80).optional(),
  lastName: shortText(80).optional(),
  timezone: shortText(64).optional(),
  avatarUrl: z.string().url().nullish()
});
var updateTutorProfileSchema = z.object({
  headline: optionalShortText(160),
  bio: longText(2e3).optional(),
  qualifications: z.array(shortText(160)).max(20).optional(),
  yearsExperience: z.coerce.number().int().min(0).max(70).optional(),
  hourlyRate: z.coerce.number().min(0).max(1e4).optional(),
  currency: z.string().length(3).optional()
});
var updateStudentProfileSchema = z.object({
  educationLevel: z.nativeEnum(EducationLevel).nullish(),
  yearGroup: optionalShortText(32),
  school: optionalShortText(160),
  targetGrade: optionalShortText(16),
  guardianName: optionalShortText(160),
  guardianEmail: emailSchema.optional(),
  guardianPhone: optionalShortText(32)
});
var inviteStudentSchema = z.object({
  email: emailSchema,
  subjectId: uuidSchema.optional(),
  message: z.string().trim().max(500).optional()
});
var studentListQuerySchema = paginationSchema.extend({
  search: z.string().trim().max(120).optional(),
  subjectId: uuidSchema.optional(),
  status: z.nativeEnum(TutorStudentStatus).optional()
});
var lessonTimesSchema = z.object({
  scheduledStart: isoDateSchema,
  scheduledEnd: isoDateSchema
}).refine((v) => new Date(v.scheduledEnd) > new Date(v.scheduledStart), {
  message: "The lesson must end after it starts",
  path: ["scheduledEnd"]
}).refine(
  (v) => new Date(v.scheduledEnd).getTime() - new Date(v.scheduledStart).getTime() <= 6 * 36e5,
  { message: "A lesson cannot be longer than 6 hours", path: ["scheduledEnd"] }
);
var createLessonSchema = z.object({
  title: shortText(160),
  description: longText(2e3).optional(),
  studentIds: z.array(uuidSchema).min(1, "Select at least one student").max(20),
  subjectId: uuidSchema.optional(),
  topicId: uuidSchema.optional(),
  objectives: z.array(shortText(200)).max(10).optional()
}).and(lessonTimesSchema);
var updateLessonSchema = z.object({
  title: shortText(160).optional(),
  description: longText(2e3).optional(),
  subjectId: uuidSchema.nullish(),
  topicId: uuidSchema.nullish(),
  objectives: z.array(shortText(200)).max(10).optional(),
  scheduledStart: isoDateSchema.optional(),
  scheduledEnd: isoDateSchema.optional(),
  status: z.nativeEnum(LessonStatus).optional()
}).refine(
  (v) => !v.scheduledStart || !v.scheduledEnd || new Date(v.scheduledEnd) > new Date(v.scheduledStart),
  { message: "The lesson must end after it starts", path: ["scheduledEnd"] }
);
var lessonListQuerySchema = paginationSchema.extend({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  status: z.nativeEnum(LessonStatus).optional(),
  studentId: uuidSchema.optional(),
  subjectId: uuidSchema.optional(),
  scope: z.enum(["all", "today", "upcoming", "past"]).default("all")
});
var cancelLessonSchema = z.object({
  reason: z.string().trim().max(500).optional()
});
var createLessonNoteSchema = z.object({
  body: longText(5e3).min(1, "Note cannot be empty"),
  isPrivate: z.boolean().default(false)
});
var createHomeworkSchema = z.object({
  title: shortText(160),
  description: longText(2e3).optional(),
  instructions: longText(1e4).optional(),
  subjectId: uuidSchema.optional(),
  topicId: uuidSchema.optional(),
  maxScore: z.coerce.number().int().min(1).max(1e3).optional(),
  estimatedMinutes: z.coerce.number().int().min(1).max(600).optional(),
  status: z.nativeEnum(HomeworkStatus).default(HomeworkStatus.PUBLISHED),
  attachmentIds: z.array(uuidSchema).max(10).optional()
});
var updateHomeworkSchema = createHomeworkSchema.partial();
var assignHomeworkSchema = z.object({
  studentIds: z.array(uuidSchema).min(1, "Select at least one student").max(50),
  dueAt: isoDateSchema,
  lessonId: uuidSchema.optional()
});
var submitHomeworkSchema = z.object({
  body: longText(2e4).optional(),
  fileIds: z.array(uuidSchema).max(10).optional()
}).refine((v) => Boolean(v.body?.trim()) || (v.fileIds?.length ?? 0) > 0, {
  message: "Add your working or attach a file before submitting",
  path: ["body"]
});
var saveHomeworkDraftSchema = z.object({
  body: longText(2e4).optional(),
  fileIds: z.array(uuidSchema).max(10).optional()
});
var markHomeworkSchema = z.object({
  score: z.coerce.number().min(0).max(1e3).optional(),
  grade: shortText(10).optional(),
  comment: longText(5e3).optional(),
  strengths: z.array(shortText(200)).max(10).optional(),
  improvements: z.array(shortText(200)).max(10).optional()
});
var homeworkListQuerySchema = paginationSchema.extend({
  status: z.nativeEnum(AssignmentStatus).optional(),
  studentId: uuidSchema.optional(),
  subjectId: uuidSchema.optional(),
  scope: z.enum(["all", "to-mark", "due", "overdue", "marked"]).default("all")
});
var presignUploadSchema = z.object({
  filename: shortText(255),
  mimeType: z.enum(ALLOWED_UPLOAD_MIME_TYPES, {
    errorMap: () => ({ message: "That file type is not supported" })
  }),
  sizeBytes: z.coerce.number().int().min(1).max(MAX_UPLOAD_BYTES, `Files must be smaller than ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`),
  purpose: z.nativeEnum(FilePurpose),
  lessonId: uuidSchema.optional()
});
var createResourceSchema = z.object({
  title: shortText(160),
  description: longText(1e3).optional(),
  subjectId: uuidSchema.optional(),
  topicId: uuidSchema.optional(),
  fileId: uuidSchema.optional(),
  externalUrl: z.string().url().optional(),
  isShared: z.boolean().default(true)
}).refine((v) => Boolean(v.fileId) || Boolean(v.externalUrl), {
  message: "Attach a file or provide a link",
  path: ["fileId"]
});
var pointSchema = z.object({
  x: z.number().finite().min(-1e5).max(1e5),
  y: z.number().finite().min(-1e5).max(1e5)
});
var hexColour = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Colour must be a hex value");
var strokeInputSchema = z.object({
  /** Client generated so the drawing peer can render optimistically. */
  id: uuidSchema,
  pageId: uuidSchema,
  tool: z.nativeEnum(WhiteboardToolType),
  colour: hexColour,
  width: z.number().min(1).max(64),
  opacity: z.number().min(0.05).max(1).default(1),
  points: z.array(pointSchema).min(1).max(5e3),
  data: z.record(z.unknown()).optional()
});
var cursorInputSchema = z.object({
  pageId: uuidSchema,
  x: z.number().finite(),
  y: z.number().finite()
});
var createWhiteboardPageSchema = z.object({
  title: shortText(80).optional(),
  backgroundType: z.enum(["BLANK", "GRID", "LINED", "GRAPH", "IMAGE"]).default("BLANK"),
  backgroundUrl: z.string().url().optional()
});
var chatMessageInputSchema = z.object({
  body: longText(4e3).min(1, "Message cannot be empty"),
  fileId: uuidSchema.optional()
});

export { assignHomeworkSchema, cancelLessonSchema, changePasswordSchema, chatMessageInputSchema, createHomeworkSchema, createLessonNoteSchema, createLessonSchema, createResourceSchema, createWhiteboardPageSchema, cursorInputSchema, emailSchema, forgotPasswordSchema, homeworkIdParamSchema, homeworkListQuerySchema, idParamSchema, inviteStudentSchema, isoDateSchema, lessonIdParamSchema, lessonListQuerySchema, loginSchema, longText, markHomeworkSchema, optionalShortText, paginationSchema, pointSchema, presignUploadSchema, registerSchema, resetPasswordSchema, saveHomeworkDraftSchema, shortText, strokeInputSchema, strongPasswordSchema, studentIdParamSchema, studentListQuerySchema, submitHomeworkSchema, updateHomeworkSchema, updateLessonSchema, updateProfileSchema, updateStudentProfileSchema, updateTutorProfileSchema, uuidSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map